import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import BrickGrid from "@/components/app/BrickGrid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MapPin, TrendingUp, Coins, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { fmt } from "@/lib/format";

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [prop, setProp] = useState(null);
  const [bricks, setBricks] = useState([]);
  const [qty, setQty] = useState(1);
  const [buying, setBuying] = useState(false);
  const [method, setMethod] = useState("paystack"); // 'paystack' | 'stripe' | 'bank'

  const load = async () => {
    const [p, b] = await Promise.all([
      api.get(`/properties/${id}`),
      api.get(`/properties/${id}/bricks`),
    ]);
    setProp(p.data);
    setBricks(b.data);
  };

  useEffect(() => {
    load().catch(() => toast.error("Failed to load property"));
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [id]);

  const buy = async () => {
    if (!user) return (window.location.href = "/login");
    if (user.kyc_status !== "approved") {
      toast.error("Complete KYC verification to buy bricks");
      return window.location.assign("/kyc");
    }
    if (qty < 1 || qty > (prop?.available_bricks || 0)) {
      toast.error("Invalid quantity");
      return;
    }
    setBuying(true);
    try {
      if (method === "bank") {
        const amount = qty * (prop?.price_per_brick || 0);
        navigate(`/bank-transfer?property_id=${id}&num_bricks=${qty}&amount=${amount}`);
        return;
      }
      const endpoint = method === "paystack" ? "/paystack/initialize" : "/checkout/create-session";
      const { data } = await api.post(endpoint, {
        property_id: id,
        num_bricks: qty,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setBuying(false);
    }
  };

  if (!prop) return <div className="p-8 text-gray-500">Loading...</div>;

  const pct = prop.total_bricks ? (prop.sold_bricks / prop.total_bricks) * 100 : 0;
  const total = qty * (prop.price_per_brick || 0);
  const myBricks = bricks.filter((b) => user && b.owner_id === user.id);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="rounded-2xl overflow-hidden aspect-[16/11] relative">
            <img src={prop.image_url} alt={prop.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050914] to-transparent" />
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
              <MapPin className="w-4 h-4" /> {prop.location}
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-white">
              {prop.name}
            </h1>
            <p className="text-gray-400 mt-4 leading-relaxed">{prop.description}</p>

            <div className="grid grid-cols-3 gap-3 mt-6">
              <Stat icon={Coins} label="Per brick" value={fmt(prop.price_per_brick)} />
              <Stat icon={TrendingUp} label="Annual ROI" value={`${prop.roi_percentage?.toFixed(1)}%`} />
              <Stat icon={Clock} label="Rental yield" value={`${prop.rental_yield?.toFixed(1)}%`} />
            </div>

            {/* Buy box */}
            <div className="mt-6 bp-surface rounded-2xl p-5">
              <div className="flex items-baseline justify-between mb-2">
                <span className="overline">Availability</span>
                <span className="font-mono text-sm text-white">
                  {prop.available_bricks}/{prop.total_bricks}
                </span>
              </div>
              <div className="h-2 rounded-full bg-white/5 border border-white/10 overflow-hidden mb-5">
                <div
                  className="h-full bg-gradient-to-r from-[#D4AF37] to-[#FDE047]"
                  style={{ width: `${pct}%` }}
                />
              </div>

              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label className="text-gray-400">Number of bricks</Label>
                  <Input
                    data-testid="buy-qty"
                    type="number"
                    min={1}
                    max={prop.available_bricks}
                    value={qty}
                    onChange={(e) => setQty(parseInt(e.target.value || "1"))}
                    className="mt-1 bg-[#050914] border-white/10 text-white focus:border-[#D4AF37]"
                  />
                </div>
                <div>
                  <div className="overline mb-1">Total</div>
                  <div className="font-heading text-3xl text-[#D4AF37]">{fmt(total)}</div>
                </div>
              </div>

              <Button
                data-testid="buy-bricks-btn"
                onClick={buy}
                disabled={buying || prop.available_bricks === 0}
                className="w-full btn-gold rounded-full h-12 mt-4"
              >
                {prop.available_bricks === 0
                  ? "Sold out"
                  : buying
                  ? "Redirecting..."
                  : user && user.kyc_status !== "approved"
                  ? "Complete KYC to buy"
                  : method === "bank"
                  ? `Pay ₦${(qty * (prop?.price_per_brick || 0)).toLocaleString()} by transfer`
                  : `Buy ${qty} Brick${qty > 1 ? "s" : ""}`}
              </Button>
              {user && user.kyc_status === "approved" && (
                <PaymentMethodPicker method={method} setMethod={setMethod} />
              )}
              {!user && (
                <div className="text-center text-xs text-gray-500 mt-2">
                  You'll be asked to sign in first
                </div>
              )}
              {user && user.kyc_status !== "approved" && (
                <div className="text-center text-xs text-[#F59E0B] mt-2">
                  Identity verification is required before your first purchase
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Brick grid */}
        <div className="mt-16">
          <Tabs defaultValue="grid" className="w-full">
            <TabsList className="bg-white/5 border border-white/10">
              <TabsTrigger value="grid" data-testid="tab-grid">Brick grid</TabsTrigger>
              <TabsTrigger value="mine" data-testid="tab-mine">My bricks ({myBricks.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="grid" className="mt-6">
              <Legend />
              <div className="mt-4 bp-surface rounded-2xl p-4">
                <BrickGrid bricks={bricks} />
              </div>
            </TabsContent>
            <TabsContent value="mine" className="mt-6">
              {myBricks.length === 0 ? (
                <div className="text-gray-500 py-10 text-center">
                  You don't own any bricks in this property yet
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {myBricks.map((b) => (
                    <div key={b.id} className="bp-surface rounded-xl p-4">
                      <div className="font-mono text-[#D4AF37] text-xs">{b.token_id}</div>
                      <div className="text-white text-sm mt-1">Brick #{b.brick_number}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {fmt(b.purchase_price, { decimals: true })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </motion.div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bp-surface rounded-xl p-4">
      <Icon className="w-4 h-4 text-[#D4AF37] mb-2" />
      <div className="overline text-[10px]">{label}</div>
      <div className="font-heading text-lg text-white mt-1">{value}</div>
    </div>
  );
}

function PaymentMethodPicker({ method, setMethod }) {
  const opts = [
    { v: "paystack", label: "Paystack", sub: "Cards · Bank · USSD · Transfer" },
    { v: "stripe", label: "Stripe", sub: "International cards" },
    { v: "bank", label: "Bank Transfer", sub: "Zenith Bank · Manual verify" },
  ];
  return (
    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
      {opts.map((o) => (
        <button
          type="button"
          key={o.v}
          onClick={() => setMethod(o.v)}
          data-testid={`pay-method-${o.v}`}
          className={`text-left px-3 py-2.5 rounded-xl border transition ${
            method === o.v
              ? "border-[#D4AF37] bg-[#D4AF37]/10"
              : "border-white/10 bg-[#050914] hover:border-white/20"
          }`}
        >
          <div className={`text-xs font-medium ${method === o.v ? "text-[#D4AF37]" : "text-white"}`}>{o.label}</div>
          <div className="text-[10px] text-gray-500 mt-0.5">{o.sub}</div>
        </button>
      ))}
    </div>
  );
}


function Legend() {
  const items = [
    { cls: "brick-available", l: "Available" },
    { cls: "brick-sold", l: "Sold" },
    { cls: "brick-mine", l: "Mine" },
    { cls: "brick-resale", l: "On resale" },
  ];
  return (
    <div className="flex flex-wrap gap-4 text-xs text-gray-400">
      {items.map((i) => (
        <div key={i.l} className="flex items-center gap-2">
          <div className={`${i.cls} w-4 h-4 rounded-sm`} /> {i.l}
        </div>
      ))}
    </div>
  );
}
